// Notification utilities for BuildMyShifts

import { collection, addDoc, query, where, orderBy, onSnapshot, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';

export const NOTIFICATION_TYPES = {
  SHIFT_ASSIGNED: 'shift_assigned',
  SHIFT_UPDATED: 'shift_updated',
  SHIFT_CANCELLED: 'shift_cancelled',
  ROTA_PUBLISHED: 'rota_published',
  SWAP_REQUEST: 'swap_request',
  SWAP_APPROVED: 'swap_approved',
  SWAP_REJECTED: 'swap_rejected',
  LEAVE_APPROVED: 'leave_approved',
  LEAVE_REJECTED: 'leave_rejected',
  SHIFT_REMINDER: 'shift_reminder',
  COVERAGE_GAP: 'coverage_gap',
  AI_SUGGESTION: 'ai_suggestion',
};

/**
 * Send a notification to a specific user
 */
export async function sendNotification(companyId, userId, type, data = {}) {
  try {
    await addDoc(collection(db, 'companies', companyId, 'shiftsNotifications'), {
      userId,
      type,
      title: data.title || getDefaultTitle(type),
      message: data.message || '',
      read: false,
      shiftId: data.shiftId || null,
      rotaId: data.rotaId || null,
      swapRequestId: data.swapRequestId || null,
      createdAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error sending notification:', error);
  }
}

/**
 * Subscribe to notifications for a user
 */
export function subscribeToNotifications(companyId, userId, callback) {
  if (!companyId || !userId) {
    callback([]);
    return () => {};
  }

  let unsub = null;
  let retryTimeout = null;
  let retries = 0;
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;

  function startListener() {
    const q = query(
      collection(db, 'companies', companyId, 'shiftsNotifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    unsub = onSnapshot(q, (snapshot) => {
      retries = 0; // reset on success
      const notifications = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
      }));
      callback(notifications);
    }, (error) => {
      // Permission errors right after account creation — retry a few times
      if (retries < MAX_RETRIES && error?.code === 'permission-denied') {
        retries++;
        if (unsub) unsub();
        retryTimeout = setTimeout(startListener, RETRY_DELAY);
      } else {
        console.error('Error listening to notifications:', error);
      }
      callback([]);
    });
  }

  startListener();

  return () => {
    if (unsub) unsub();
    if (retryTimeout) clearTimeout(retryTimeout);
  };
}

/**
 * Mark a notification as read
 */
export async function markAsRead(companyId, notificationId) {
  try {
    await updateDoc(doc(db, 'companies', companyId, 'shiftsNotifications', notificationId), {
      read: true,
      readAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
  }
}

function getDefaultTitle(type) {
  const titles = {
    [NOTIFICATION_TYPES.SHIFT_ASSIGNED]: 'Shift Assigned',
    [NOTIFICATION_TYPES.SHIFT_UPDATED]: 'Shift Updated',
    [NOTIFICATION_TYPES.SHIFT_CANCELLED]: 'Shift Cancelled',
    [NOTIFICATION_TYPES.ROTA_PUBLISHED]: 'Rota Published',
    [NOTIFICATION_TYPES.SWAP_REQUEST]: 'Swap Request',
    [NOTIFICATION_TYPES.SWAP_APPROVED]: 'Swap Approved',
    [NOTIFICATION_TYPES.SWAP_REJECTED]: 'Swap Rejected',
    [NOTIFICATION_TYPES.LEAVE_APPROVED]: 'Leave Approved',
    [NOTIFICATION_TYPES.LEAVE_REJECTED]: 'Leave Rejected',
    [NOTIFICATION_TYPES.SHIFT_REMINDER]: 'Shift Reminder',
    [NOTIFICATION_TYPES.COVERAGE_GAP]: 'Coverage Gap Detected',
    [NOTIFICATION_TYPES.AI_SUGGESTION]: 'AI Suggestion',
  };
  return titles[type] || 'Notification';
}

const notificationUtils = {
  NOTIFICATION_TYPES,
  sendNotification,
  subscribeToNotifications,
  markAsRead,
};
export default notificationUtils;
